import {
  LEGACY_STORAGE_KEY,
  SAVED_DOCUMENT_VERSION,
  WORKSPACE_DOCUMENT_VERSION,
  boundedText,
  cloneValue,
  collectionKey,
  contractFor,
  decodePayload,
  defaultClock,
  defaultCreateId,
  detachedSource,
  exactFields,
  fixedRows,
  frozenClone,
  isRecord,
  isoTimestamp,
  opaqueId,
  payloadsEqual,
  providerFailure,
  runtimeFixedPresetDescriptors,
  savedPresetName,
  timestampRange,
} from "./preset-internals.js";

export { LEGACY_STORAGE_KEY } from "./preset-internals.js";

export const LEGACY_PRESET_MIGRATION_ID = "legacy-user-presets-v1";
export const LEGACY_PRESET_MIGRATION_RECEIPT_VERSION = 1;

const ROOT_FIELDS = Object.freeze(["collections"]);
const RECEIPT_FIELDS = Object.freeze([
  "counts",
  "id",
  "importedAt",
  "receiptVersion",
  "sourceKey",
  "sourcePresent",
]);
const RECEIPT_COUNT_FIELDS = Object.freeze([
  "collections",
  "presets",
  "quarantined",
  "workingCopies",
]);
const COLLECTION_FIELDS = Object.freeze(["activeWorkingCopyId", "presets", "workingCopies"]);
const PRESET_FIELDS = Object.freeze(["createdAt", "id", "name", "payload", "updatedAt"]);
const WORKING_COPY_FIELDS = Object.freeze([
  "createdAt",
  "events",
  "id",
  "payload",
  "source",
  "temporary",
  "undoneEvents",
  "updatedAt",
]);
const EVENT_FIELDS = Object.freeze([
  "action",
  "afterPayload",
  "at",
  "beforePayload",
  "id",
  "source",
]);
const SOURCE_FIELDS = Object.freeze(["id", "kind"]);

export function decodeLegacyPresetMigrationReceipt(value) {
  if (!exactFields(value, RECEIPT_FIELDS)) {
    throw new TypeError("preset-migration-receipt-fields-invalid");
  }
  if (
    value.receiptVersion !== LEGACY_PRESET_MIGRATION_RECEIPT_VERSION
    || value.id !== LEGACY_PRESET_MIGRATION_ID
    || value.sourceKey !== LEGACY_STORAGE_KEY
    || typeof value.sourcePresent !== "boolean"
    || !exactFields(value.counts, RECEIPT_COUNT_FIELDS)
  ) {
    throw new TypeError("preset-migration-receipt-contract-invalid");
  }
  const counts = {};
  for (const field of RECEIPT_COUNT_FIELDS) {
    if (!Number.isSafeInteger(value.counts[field]) || value.counts[field] < 0) {
      throw new TypeError("preset-migration-receipt-counts-invalid");
    }
    counts[field] = value.counts[field];
  }
  return {
    receiptVersion: LEGACY_PRESET_MIGRATION_RECEIPT_VERSION,
    id: LEGACY_PRESET_MIGRATION_ID,
    sourceKey: LEGACY_STORAGE_KEY,
    sourcePresent: value.sourcePresent,
    importedAt: isoTimestamp(value.importedAt, "Preset migration acknowledgement time"),
    counts,
  };
}

function strictLegacyPayload(contract, value) {
  if (typeof contract.migrateLegacyPayload !== "function") {
    throw new TypeError("collection-has-no-legacy-payload-migrator");
  }
  const payload = contract.migrateLegacyPayload(cloneValue(value));
  return decodePayload(contract, payload);
}

function legacySource(raw) {
  if (!exactFields(raw, SOURCE_FIELDS)) {
    throw new TypeError("legacy-source-fields-invalid");
  }
  if (raw.kind === "none") {
    if (raw.id !== "") {
      throw new TypeError("legacy-detached-source-id-invalid");
    }
    return { kind: "none", id: "" };
  }
  if (raw.kind !== "preset" && raw.kind !== "fixed") {
    throw new TypeError("legacy-source-kind-invalid");
  }
  return { kind: raw.kind, id: opaqueId(raw.id, "Legacy source ID") };
}

function decodeLegacyPreset(contract, raw) {
  if (!exactFields(raw, PRESET_FIELDS)) {
    throw new TypeError("legacy-preset-fields-invalid");
  }
  const timestamps = timestampRange(raw.createdAt, raw.updatedAt, "Legacy preset");
  return {
    id: opaqueId(raw.id, "Legacy preset ID"),
    name: savedPresetName(raw.name, "Legacy preset name"),
    payload: strictLegacyPayload(contract, raw.payload),
    ...timestamps,
  };
}

function decodeLegacyEvent(contract, raw) {
  if (!exactFields(raw, EVENT_FIELDS)) {
    throw new TypeError("legacy-history-event-fields-invalid");
  }
  return {
    id: opaqueId(raw.id, "Legacy history event ID"),
    action: boundedText(raw.action, "Legacy history action", { max: 96 }),
    at: isoTimestamp(raw.at, "Legacy history event time"),
    source: legacySource(raw.source),
    beforePayload: raw.beforePayload === null
      ? null
      : strictLegacyPayload(contract, raw.beforePayload),
    afterPayload: raw.afterPayload === null
      ? null
      : strictLegacyPayload(contract, raw.afterPayload),
  };
}

function decodeLegacyWorkingCopy(contract, raw) {
  if (!exactFields(raw, WORKING_COPY_FIELDS)) {
    throw new TypeError("legacy-working-copy-fields-invalid");
  }
  if (typeof raw.temporary !== "boolean") {
    throw new TypeError("legacy-working-copy-temporary-invalid");
  }
  if (!Array.isArray(raw.events) || !Array.isArray(raw.undoneEvents)) {
    throw new TypeError("legacy-working-copy-history-invalid");
  }
  const timestamps = timestampRange(
    raw.createdAt,
    raw.updatedAt,
    "Legacy working copy",
  );
  return {
    id: opaqueId(raw.id, "Legacy working-copy ID"),
    source: legacySource(raw.source),
    payload: raw.payload === null ? null : strictLegacyPayload(contract, raw.payload),
    temporary: raw.temporary,
    ...timestamps,
    events: raw.events.map((event) => decodeLegacyEvent(contract, event)),
    undoneEvents: raw.undoneEvents.map((event) => decodeLegacyEvent(contract, event)),
  };
}

function sourceBase(contract, source, presets) {
  if (source.kind === "preset") {
    const preset = presets.find((candidate) => candidate.id === source.id) || null;
    return { known: preset !== null, payload: preset?.payload || null };
  }
  if (source.kind === "fixed") {
    const preset = fixedRows(contract).find((candidate) => candidate.id === source.id) || null;
    const deferred = runtimeFixedPresetDescriptors(contract)
      .some((descriptor) => descriptor.id === source.id);
    return {
      known: preset !== null || deferred,
      payload: preset?.payload || null,
    };
  }
  return { known: false, payload: null };
}

function historyIsValid(contract, basePayload, orderedEvents, cursor, currentPayload) {
  let payload = basePayload;
  const cursorPayloads = [payload];
  for (const event of orderedEvents) {
    if (
      !event.beforePayload
      || !event.afterPayload
      || !payloadsEqual(contract, payload, event.beforePayload)
    ) {
      return false;
    }
    payload = event.afterPayload;
    cursorPayloads.push(payload);
  }
  return Boolean(
    currentPayload
    && cursorPayloads[cursor]
    && payloadsEqual(contract, cursorPayloads[cursor], currentPayload),
  );
}

function makeRevision({ createId, changeId, parentRevisionId, event, payload, at, actionKind = "base" }) {
  return {
    revisionId: opaqueId(createId("workspace_revision"), "Migrated workspace revision ID"),
    changeId,
    parentRevisionId,
    supersedesRevisionId: null,
    actionId: event
      ? event.id
      : opaqueId(createId("preset_action"), "Migrated preset action ID"),
    actionKind: event?.action || actionKind,
    at,
    basedOn: detachedSource(),
    compacted: false,
    payload: cloneValue(payload),
  };
}

function migrateWorkingCopy({ contract, workingCopy, presets, createId, migrationTime }) {
  const resolvedSource = sourceBase(contract, workingCopy.source, presets);
  const orderedEvents = [
    ...workingCopy.events,
    ...workingCopy.undoneEvents.slice().reverse(),
  ];
  const inferredBase = resolvedSource.payload
    || orderedEvents[0]?.beforePayload
    || workingCopy.payload;
  if (!inferredBase || !workingCopy.payload) {
    throw new TypeError("legacy-working-copy-has-no-valid-payload");
  }
  const changeId = opaqueId(createId("workspace_change"), "Migrated workspace change ID");
  const base = makeRevision({
    createId,
    changeId,
    parentRevisionId: null,
    payload: inferredBase,
    at: workingCopy.createdAt,
  });
  const revisions = [base];
  let headRevisionId = base.revisionId;
  let historyInvalid = false;
  if (historyIsValid(
    contract,
    inferredBase,
    orderedEvents,
    workingCopy.events.length,
    workingCopy.payload,
  )) {
    let parentRevisionId = base.revisionId;
    const eventRevisionIds = [];
    for (const event of orderedEvents) {
      const revision = makeRevision({
        createId,
        changeId,
        parentRevisionId,
        event,
        payload: event.afterPayload,
        at: event.at,
      });
      revisions.push(revision);
      eventRevisionIds.push(revision.revisionId);
      parentRevisionId = revision.revisionId;
    }
    headRevisionId = workingCopy.events.length === 0
      ? base.revisionId
      : eventRevisionIds[workingCopy.events.length - 1];
  } else if (!payloadsEqual(contract, inferredBase, workingCopy.payload)) {
    historyInvalid = orderedEvents.length > 0;
    const latest = makeRevision({
      createId,
      changeId,
      parentRevisionId: base.revisionId,
      payload: workingCopy.payload,
      at: workingCopy.updatedAt || migrationTime,
      actionKind: "legacy-current",
    });
    revisions.push(latest);
    headRevisionId = latest.revisionId;
  } else {
    historyInvalid = orderedEvents.length > 0;
  }
  const declaredSource = workingCopy.temporary ? detachedSource() : workingCopy.source;
  const basedOn = declaredSource.kind !== "none" && resolvedSource.known
    ? { kind: declaredSource.kind, id: declaredSource.id, revisionId: base.revisionId }
    : detachedSource();
  for (const revision of revisions) {
    revision.basedOn = cloneValue(basedOn);
  }
  return {
    changeId,
    baseRevisionId: base.revisionId,
    headRevisionId,
    revisions,
    historyInvalid,
  };
}

function quarantineRow({ createId, clock, collectionKey: key, reason, rawBytes, path, id = undefined }) {
  return {
    id: opaqueId(id ?? createId("preset_quarantine"), "Preset quarantine ID"),
    collectionKey: key,
    kind: "legacy-user-presets-v1",
    reason,
    path,
    rawBytes,
    quarantinedAt: isoTimestamp(clock(), "Preset quarantine time"),
  };
}

export function buildLegacyPresetImport(rawBytes, {
  collectionContracts,
  clock = defaultClock,
  createId = defaultCreateId,
} = {}) {
  if (typeof rawBytes !== "string") {
    throw new TypeError("Legacy preset input must be the original text bytes.");
  }
  const importedAt = isoTimestamp(clock(), "Preset migration time");
  let root;
  try {
    root = JSON.parse(rawBytes);
  } catch (_error) {
    return {
      workspaceRecords: [],
      savedRecords: [],
      quarantine: [quarantineRow({
        createId,
        clock,
        collectionKey: "legacy-root",
        reason: "malformed-json",
        rawBytes,
        path: "$",
      })],
      counts: { collections: 0, presets: 0, workingCopies: 0, quarantined: 1 },
    };
  }
  if (!exactFields(root, ROOT_FIELDS) || !isRecord(root.collections)) {
    return {
      workspaceRecords: [],
      savedRecords: [],
      quarantine: [quarantineRow({
        createId,
        clock,
        collectionKey: "legacy-root",
        reason: "root-fields-invalid",
        rawBytes,
        path: "$",
      })],
      counts: { collections: 0, presets: 0, workingCopies: 0, quarantined: 1 },
    };
  }

  const workspaceRecords = [];
  const savedRecords = [];
  const quarantine = [];
  let presetCount = 0;
  let workingCopyCount = 0;

  for (const [rawKey, rawCollection] of Object.entries(root.collections)) {
    let key;
    let contract;
    try {
      key = collectionKey(rawKey);
      contract = contractFor(collectionContracts, key);
    } catch (_error) {
      quarantine.push(quarantineRow({
        createId,
        clock,
        collectionKey: "legacy-unknown",
        reason: "unknown-collection",
        rawBytes,
        path: `$.collections.${rawKey}`,
      }));
      continue;
    }
    if (
      !exactFields(rawCollection, COLLECTION_FIELDS)
      || !Array.isArray(rawCollection.presets)
      || !Array.isArray(rawCollection.workingCopies)
      || typeof rawCollection.activeWorkingCopyId !== "string"
    ) {
      quarantine.push(quarantineRow({
        createId,
        clock,
        collectionKey: key,
        reason: "collection-fields-invalid",
        rawBytes,
        path: `$.collections.${key}`,
      }));
      continue;
    }

    const presets = [];
    const presetIds = new Set();
    rawCollection.presets.forEach((rawPreset, index) => {
      try {
        const preset = decodeLegacyPreset(contract, rawPreset);
        if (presetIds.has(preset.id)) {
          throw new TypeError("legacy-preset-id-duplicated");
        }
        presetIds.add(preset.id);
        presets.push(preset);
      } catch (error) {
        quarantine.push(quarantineRow({
          createId,
          clock,
          collectionKey: key,
          reason: error.message || "preset-invalid",
          rawBytes,
          path: `$.collections.${key}.presets[${index}]`,
        }));
      }
    });
    if (presets.length) {
      savedRecords.push({
        documentVersion: SAVED_DOCUMENT_VERSION,
        collectionKey: key,
        revision: opaqueId(createId("saved_collection_revision"), "Migrated saved revision"),
        presets: presets.map((preset) => ({
          ...preset,
          revision: opaqueId(createId("preset_revision"), "Migrated preset revision"),
          payloadRevisionId: opaqueId(createId("saved_payload_revision"), "Migrated payload revision"),
          tombstone: false,
        })),
      });
      presetCount += presets.length;
    }

    const migratedCopies = [];
    rawCollection.workingCopies.forEach((rawWorkingCopy, index) => {
      try {
        const workingCopy = decodeLegacyWorkingCopy(contract, rawWorkingCopy);
        const migrated = migrateWorkingCopy({
          contract,
          workingCopy,
          presets,
          createId,
          migrationTime: importedAt,
        });
        migratedCopies.push({ workingCopy, migrated });
        workingCopyCount += 1;
        if (migrated.historyInvalid) {
          quarantine.push(quarantineRow({
            createId,
            clock,
            collectionKey: key,
            reason: "history-adjacency-or-cursor-invalid",
            rawBytes,
            path: `$.collections.${key}.workingCopies[${index}].history`,
          }));
        }
      } catch (error) {
        quarantine.push(quarantineRow({
          createId,
          clock,
          collectionKey: key,
          reason: error.message || "working-copy-invalid",
          rawBytes,
          path: `$.collections.${key}.workingCopies[${index}]`,
        }));
      }
    });

    if (migratedCopies.length) {
      const active = migratedCopies.find(({ workingCopy }) => (
        workingCopy.id === rawCollection.activeWorkingCopyId
      )) || migratedCopies[0];
      workspaceRecords.push({
        documentVersion: WORKSPACE_DOCUMENT_VERSION,
        collectionKey: key,
        storeRevision: opaqueId(createId("workspace_store_revision"), "Migrated workspace store revision"),
        headRevisionId: active.migrated.headRevisionId,
        headChangeId: active.migrated.changeId,
        revisions: migratedCopies.flatMap(({ migrated }) => migrated.revisions),
        lastVisitedChildren: [],
        createdAt: active.workingCopy.createdAt,
        updatedAt: active.workingCopy.updatedAt,
      });
    }
  }

  return {
    workspaceRecords,
    savedRecords,
    quarantine,
    counts: {
      collections: new Set([
        ...workspaceRecords.map((record) => record.collectionKey),
        ...savedRecords.map((record) => record.collectionKey),
      ]).size,
      presets: presetCount,
      workingCopies: workingCopyCount,
      quarantined: quarantine.length,
    },
  };
}

export async function migrateLegacyPresetStorage({
  provider,
  storage,
  collectionContracts,
  clock = defaultClock,
  createId = defaultCreateId,
} = {}) {
  if (
    !provider
    || typeof provider.migrationReceipt !== "function"
    || typeof provider.atomicLegacyImport !== "function"
  ) {
    throw new TypeError("Legacy preset migration requires an IndexedDB preset provider.");
  }
  let existingReceipt;
  try {
    existingReceipt = await provider.migrationReceipt(LEGACY_PRESET_MIGRATION_ID);
  } catch (error) {
    return frozenClone(providerFailure("read-preset-migration", error));
  }

  if (existingReceipt !== null && existingReceipt !== undefined) {
    let receipt;
    try {
      receipt = decodeLegacyPresetMigrationReceipt(existingReceipt);
    } catch (error) {
      return frozenClone({
        kind: "invalid",
        boundary: "preset-migration-receipt",
        reason: error.message || "preset-migration-receipt-invalid",
      });
    }
    try {
      if (
        storage
        && typeof storage.getItem === "function"
        && typeof storage.removeItem === "function"
        && storage.getItem(LEGACY_STORAGE_KEY) !== null
      ) {
        storage.removeItem(LEGACY_STORAGE_KEY);
      }
    } catch (_error) {
      // The receipt is authoritative; inaccessible legacy storage cannot undo the cutover.
    }
    return frozenClone({ kind: "acknowledged", imported: false, receipt });
  }

  let rawBytes;
  try {
    if (!storage || typeof storage.getItem !== "function") {
      throw new TypeError("Legacy storage is unavailable.");
    }
    rawBytes = storage.getItem(LEGACY_STORAGE_KEY);
  } catch (_error) {
    return frozenClone({
      kind: "unavailable",
      operation: "read-legacy-presets",
      reason: "legacy-storage-read-failed",
    });
  }

  const plan = rawBytes === null
    ? { workspaceRecords: [], savedRecords: [], quarantine: [], counts: {
      collections: 0,
      presets: 0,
      workingCopies: 0,
      quarantined: 0,
    } }
    : buildLegacyPresetImport(rawBytes, { collectionContracts, clock, createId });
  const receipt = {
    receiptVersion: LEGACY_PRESET_MIGRATION_RECEIPT_VERSION,
    id: LEGACY_PRESET_MIGRATION_ID,
    sourceKey: LEGACY_STORAGE_KEY,
    sourcePresent: rawBytes !== null,
    importedAt: isoTimestamp(clock(), "Preset migration acknowledgement time"),
    counts: cloneValue(plan.counts),
  };
  let imported;
  try {
    const collisionQuarantine = rawBytes === null
      ? []
      : Array.from(new Set([
          ...plan.workspaceRecords.map((record) => record.collectionKey),
          ...plan.savedRecords.map((record) => record.collectionKey),
        ])).map((key) => quarantineRow({
          createId,
          clock,
          collectionKey: key,
          reason: "target-collection-already-exists",
          rawBytes,
          path: `$.collections.${key}`,
          id: `legacy_target_collision_${key}`,
        }));
    imported = await provider.atomicLegacyImport({
      workspaceRecords: plan.workspaceRecords,
      savedRecords: plan.savedRecords,
      quarantine: plan.quarantine,
      collisionQuarantine,
      receipt,
    });
  } catch (error) {
    return frozenClone(providerFailure("write-preset-migration", error));
  }

  if (imported?.collision) {
    return frozenClone({
      kind: "conflict",
      operation: "migrate-legacy-presets",
      reason: "preset-migration-target-collision",
      collectionKeys: cloneValue(imported.collision.collectionKeys),
    });
  }
  let importedReceipt;
  try {
    importedReceipt = decodeLegacyPresetMigrationReceipt(imported?.receipt);
  } catch (error) {
    return frozenClone({
      kind: "invalid",
      boundary: "preset-migration-receipt",
      reason: error.message || "preset-migration-receipt-invalid",
    });
  }

  if (rawBytes !== null) {
    try {
      storage.removeItem(LEGACY_STORAGE_KEY);
    } catch (_error) {
      return frozenClone({
        kind: "unavailable",
        operation: "remove-legacy-presets",
        reason: "legacy-storage-remove-failed",
        receipt: importedReceipt,
      });
    }
  }
  return frozenClone({
    kind: "acknowledged",
    imported: imported.imported,
    receipt: importedReceipt,
  });
}
